import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Copy, Check, AlertTriangle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";

/**
 * Admin panel — Syncro webhook health.
 *
 * Answers the question "why do I keep needing to backfill?" by
 * surfacing (a) the exact URLs to paste into Syncro admin, (b) the
 * last-received timestamp with a colour-coded verdict, and (c) the
 * 30-day accrued/dropped counts so admins can tell "not firing at
 * all" from "firing but hitting drop rules". If the verdict is
 * ``never`` we render a red setup banner so the missing step is
 * impossible to miss.
 */
export default function SyncroWebhookHealthPanel() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/commissions/webhook-health");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load webhook health");
      setData({ error: true });
    }
  }
  useEffect(() => { load(); }, []);

  function copyUrl(kind, url) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(kind);
      toast.success(`${kind} URL copied`);
      setTimeout(() => setCopied(""), 1500);
    });
  }

  if (data === null) {
    return (
      <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <div className="text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading webhook health…
        </div>
      </section>
    );
  }
  if (data.error) return null;

  const verdict = data.verdict; // never | stale | healthy
  const verdictConfig = {
    never:   { color: "text-red-300",     bg: "bg-red-500/15",     border: "border-red-500/40",     label: "Never received", icon: AlertTriangle },
    stale:   { color: "text-amber-300",   bg: "bg-amber-500/15",   border: "border-amber-500/40",   label: "Stale (>7 days)", icon: AlertTriangle },
    healthy: { color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/40", label: "Receiving", icon: CheckCircle2 },
  }[verdict];
  const VIcon = verdictConfig.icon;

  const lastReceived = data.last_payment_made?.received_at || data.last_webhook_any?.received_at;
  const lastReceivedFmt = lastReceived
    ? new Date(lastReceived).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })
    : "—";
  const daysAgo = lastReceived
    ? Math.floor((Date.now() - new Date(lastReceived).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <section data-testid="syncro-webhook-health-panel" className="rounded-xl border border-slate-800 bg-slate-900 p-5 md:p-6 space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold inline-flex items-center gap-1.5">
            <Radio className="w-3.5 h-3.5" /> Syncro webhooks
          </div>
          <h2 className="font-display text-xl mt-1">Live payment feed</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            When Syncro is wired up correctly, every paid invoice hits your ledger in seconds — no manual backfill needed.
            The nightly backfill cron is a safety-net that catches anything the webhook dropped, not a primary path.
          </p>
        </div>
        <div className={`shrink-0 inline-flex items-center gap-2 rounded-full border ${verdictConfig.border} ${verdictConfig.bg} px-3 py-1.5`}>
          <VIcon className={`w-4 h-4 ${verdictConfig.color}`} />
          <span className={`text-xs font-semibold ${verdictConfig.color}`} data-testid="webhook-verdict">
            {verdictConfig.label}
          </span>
        </div>
      </header>

      {/* Big red setup banner when nothing's ever landed */}
      {verdict === "never" && (
        <div data-testid="webhook-setup-banner" className="rounded-lg border border-red-500/40 bg-red-500/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-200 space-y-2">
              <div className="font-semibold text-red-200">Syncro has never fired a webhook at this app.</div>
              <div className="text-slate-300 text-xs leading-relaxed">
                Every commission event you&rsquo;ve seen so far came from the manual/nightly backfill — not from live Syncro payments.
                To go fully autonomous:
              </div>
              <ol className="text-xs text-slate-300 list-decimal list-inside space-y-1 pl-1">
                <li>Open Syncro admin → <b>Admin → Notification Center → New notification</b>.</li>
                <li>Event: <b>Payment Made</b>. Delivery method: <b>Webhook</b>.</li>
                <li>Paste the <b>Payment made</b> URL below into the Webhook URL field.</li>
                <li>Save. Optionally repeat for <i>Payment Failed</i> and <i>Payment Disputed</i> to catch refunds/clawbacks automatically.</li>
              </ol>
              <div className="text-[11px] text-slate-500 italic">The webhook secret in the URL is a shared token — treat it like a password.</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Last payment webhook" value={lastReceivedFmt} sub={daysAgo != null ? `${daysAgo}d ago` : ""} testid="webhook-last-received" />
        <StatTile label="Accrued (30d)" value={data.counts_30d?.accrued ?? 0} accent="cyan" testid="webhook-30d-accrued" />
        <StatTile label="Dropped (30d)" value={data.counts_30d?.dropped ?? 0} accent={data.counts_30d?.dropped > 0 ? "amber" : undefined} testid="webhook-30d-dropped" />
        <StatTile label="Backfill safety-net" value="Nightly 04:00 AEDT" sub="cron auto-heals gaps" testid="webhook-backfill-note" />
      </div>

      {/* URL rows */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Webhook URLs (paste into Syncro)</div>
        {[
          { kind: "Payment made",     url: data.webhook_urls.payment_made,     required: true },
          { kind: "Payment failed",   url: data.webhook_urls.payment_failed,   required: false },
          { kind: "Payment disputed", url: data.webhook_urls.payment_dispute,  required: false },
        ].map((row) => (
          <div key={row.kind} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2">
            <div className="text-[11px] font-semibold text-slate-300 shrink-0 w-32">
              {row.kind}
              {row.required && <span className="text-red-300 ml-1">*</span>}
            </div>
            <code className="flex-1 font-mono text-[11px] text-cyan-200 truncate" title={row.url}>
              {row.url}
            </code>
            <button
              type="button"
              onClick={() => copyUrl(row.kind, row.url)}
              data-testid={`webhook-copy-${row.kind.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-1 rounded-md border border-slate-700 hover:border-cyan-500 bg-slate-900 hover:bg-slate-800 px-2 py-1 text-xs text-slate-300 transition-colors"
            >
              {copied === row.kind ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              {copied === row.kind ? "Copied" : "Copy"}
            </button>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-slate-500 border-t border-slate-800 pt-3 flex items-center gap-2">
        <ExternalLink className="w-3 h-3" />
        <span>Not sure where to paste these? Syncro docs: <a href="https://support.syncromsp.com/hc/en-us/articles/360036809591" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-200 underline">Notification Center → Webhooks</a>.</span>
      </div>
    </section>
  );
}

function StatTile({ label, value, sub, accent, testid }) {
  const colorMap = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
  };
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono text-sm mt-1 truncate ${colorMap[accent] || "text-slate-100"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
